/**
 * mix-readiness.js — window.MixReadiness
 *
 * The "is this track ready to release?" layer of /mix-analyzer. Where
 * mix-analyzer-engine.js answers «чем мой трек отличается от эталона»,
 * this file answers «что в нём объективно сломано» and «типично ли это
 * для жанра» — WITHOUT needing a reference track at all.
 *
 * Born from a user's mastering checklist (2026-08-09): 7 blocks / 40 rows of
 * norms and "disqualification" thresholds. Most of the numbers he needed were
 * ALREADY measured by the platform and simply never surfaced as a verdict.
 *
 * ── Why three blocks and not one pass/fail score ──────────────────────────
 * The source checklist mixed two incompatible classes of check, and shipping
 * them as one grade would make the tool lie confidently:
 *
 *   DELIVERY  — objective spec. True peak, clipping, DC, edit clicks, hum,
 *               phase. A file either violates these or it doesn't, in every
 *               genre. Hard pass/warn/fail is honest here.
 *   DEFECTS   — objective-ish artefacts. Resonances, sibilance humps, rumble,
 *               mono collapse. Genre-independent, but severity is a judgement
 *               call, so these advise, never "fail".
 *   BALANCE   — artistic targets. LUFS, LRA, DR, tonal tilt, arrangement.
 *               These are ONLY meaningful against a genre. The checklist said
 *               "LUFS < −16 = дисквалификация", which would fail every jazz
 *               and classical master ever made — our own GENRE_TARGETS put
 *               jazz at −16 and classical at −20. So balance rows are phrased
 *               as "нетипично для жанра" and can never fail a track.
 *
 * ── What the corpus said (2026-08-09, 34 real mastered tracks) ───────────
 * Every row here survived TWO tests on real music: it must stay quiet on
 * finished masters AND still fire when the defect is planted into the same
 * material. Four rows failed and were REMOVED rather than shipped blind —
 * resonance, sibilance, air-hump and mid-dip. Details at each site; the short
 * version is that a detector which can only ever answer "ok" tells the user
 * their track was checked when it was not.
 *
 * Rows also dropped from the source checklist:
 * · 7.6 "+6 дБ — не разваливается" — every master clips when you add 6 dB.
 * · 6.3 verse/chorus contrast overlaps LRA, so it is measured as loudest-10s
 *   minus quietest-10s to describe SECTIONS rather than reprint LRA.
 *
 * Pure measurement + rule layer: no DOM, no i18n. Every row is a
 * { id, block, status, args } record; the blade maps `id` → a localized
 * string. Reuses validated platform DSP rather than re-deriving it:
 *   - LoudnessR128            (BS.1770-4 LUFS / LRA / 4× oversampled true peak)
 *   - AudioComparatorEngine   (Welch PSD, short-term LUFS over time)
 *   - MixAnalyzer.analyze()   (the metric bundle this file grades)
 *
 * ⚠️ RestorationDiagnose is deliberately NOT used, though it looks like the
 * obvious prior art. Measured 2026-08-09: its `noiseFloorDb` reads −13.7 dBFS
 * ("bad" by its own severity map) on a dense gapless master — it reports the
 * MUSIC as noise and would fail nearly every commercial master; its `mainsHz`
 * found a 59 Hz "hum" at −31 dB in pink noise containing none; and its click
 * detector fired on 34/34 finished masters. All three are right for archival
 * tape and wrong for a finished mix. Noise is measured here (noiseFloorEstimate),
 * clicks here (clickRate), and hum is not claimed at all.
 */
(function () {
    'use strict';

    // Status vocabulary. Delivery rows use pass/warn/fail; defect and balance
    // rows use ok/note/strong — deliberately NOT "fail", see the header note.
    var PASS = 'pass', WARN = 'warn', FAIL = 'fail';
    var OK = 'ok', NOTE = 'note', STRONG = 'strong';
    // "Не измерить на этом материале" — a first-class outcome, not a pass.
    // A check that silently reports PASS when it could not run is how a
    // verdict tool starts lying.
    var NA = 'na';

    /* ── Small numeric helpers ─────────────────────────────────────────── */

    function db10(x) { return x > 0 ? 10 * Math.log10(x) : -Infinity; }
    function db20(x) { return x > 0 ? 20 * Math.log10(x) : -Infinity; }

    // toFixed() renders anything in (−0.05, 0) as "-0.0", which reads to a
    // user as a real (if tiny) loss rather than as nothing at all. Every
    // number that reaches the page goes through here.
    function fx(v, digits) {
        if (!isFinite(v)) return '';
        var s = v.toFixed(digits == null ? 1 : digits);
        return /^-0(\.0+)?$/.test(s) ? s.slice(1) : s;
    }

    // Percentile of a plain numeric array (p in 0..1). Sorts a copy.
    function percentile(arr, p) {
        if (!arr || !arr.length) return NaN;
        var s = arr.slice().sort(function (a, b) { return a - b; });
        var idx = (s.length - 1) * p;
        var lo = Math.floor(idx), hi = Math.ceil(idx);
        if (lo === hi) return s[lo];
        return s[lo] + (s[hi] - s[lo]) * (idx - lo);
    }

    /* ── PSD band helpers ──────────────────────────────────────────────────
     * `psd` is AudioComparatorEngine.computeAveragePsd output:
     *   { freqHz: Float32Array, psdDb: Float32Array, sampleRate, fftSize }
     * Bins are LINEARLY spaced, so a plain bin average over a range is an
     * average spectral DENSITY — bandwidth-fair, which matters when comparing
     * a 3 kHz-wide window against a 2 kHz-wide one.
     */

    // Mean energy density of [lo, hi) in dB. Averages in the LINEAR domain
    // (dB-averaging would under-weight peaks and read low on tonal material).
    function bandDensityDb(psd, lo, hi) {
        if (!psd || !psd.freqHz || !psd.freqHz.length) return NaN;
        var f = psd.freqHz, d = psd.psdDb;
        var sum = 0, n = 0;
        for (var i = 0; i < f.length; i++) {
            if (f[i] >= lo && f[i] < hi) { sum += Math.pow(10, d[i] / 10); n++; }
        }
        return n > 0 ? db10(sum / n) : NaN;
    }

    /**
     * TOTAL energy of [lo, hi) in dB — the integral, not the average.
     *
     * Density and total answer different questions and the choice is not
     * cosmetic. "Is there more energy under 30 Hz than between 30 and 60?" is
     * a question about totals: a natural −3 dB/octave tilt puts a 10 Hz-wide
     * band BELOW a 30 Hz-wide one by ~2.3 dB, which is the correct baseline
     * for "no rumble". Measured as density the same pink tilt reads +2.6 dB
     * and every untouched master looks like it rumbles.
     */
    function bandEnergyDb(psd, lo, hi) {
        if (!psd || !psd.freqHz || !psd.freqHz.length) return NaN;
        var f = psd.freqHz, d = psd.psdDb;
        var sum = 0, n = 0;
        for (var i = 0; i < f.length; i++) {
            if (f[i] >= lo && f[i] < hi) { sum += Math.pow(10, d[i] / 10); n++; }
        }
        return n > 0 ? db10(sum) : NaN;
    }

    /* ── Time-domain measurements ──────────────────────────────────────── */

    /**
     * Windowed RMS level series in dBFS — 400 ms windows, long enough to
     * ignore single transients and short enough to separate a verse from a
     * chorus. Computed once and shared by the dynamics and noise checks.
     * Windows under −90 dBFS are digital silence and carry no information.
     */
    function levelSeries(buffer) {
        var sr = buffer.sampleRate, len = buffer.length;
        var win = Math.max(1, Math.round(sr * 0.4));
        if (len < win * 4) return [];
        var ch = buffer.numberOfChannels, chans = [];
        for (var c = 0; c < ch; c++) chans.push(buffer.getChannelData(c));
        var vals = [];
        for (var off = 0; off + win <= len; off += win) {
            var sum = 0;
            for (var k = 0; k < win; k++) {
                var m = 0;
                for (var ci = 0; ci < ch; ci++) m += chans[ci][off + k];
                m /= ch;
                sum += m * m;
            }
            var dbv = db20(Math.sqrt(sum / win));
            if (dbv > -90) vals.push(dbv);
        }
        return vals;
    }

    /**
     * DR as the checklist defines it: p95 − p5 of windowed RMS, in dB.
     *
     * NOT the same number as PLR (peak − loudness) or crest (peak − RMS) that
     * the compare engine already reports: this one describes how far the loud
     * moments sit above the quiet ones, so it catches "everything is the same
     * level" compression that a peak-based measure misses.
     */
    function dynamicRangeDb(bufferOrSeries) {
        var vals = Array.isArray(bufferOrSeries) ? bufferOrSeries : levelSeries(bufferOrSeries);
        // −70 dBFS gate: the run-in before the first downbeat is not dynamics.
        vals = vals.filter(function (v) { return v > -70; });
        if (vals.length < 8) return NaN;
        return percentile(vals, 0.95) - percentile(vals, 0.05);
    }

    /**
     * Noise in the gaps (checklist 6.6) — and, just as importantly, whether
     * the question can be answered at all for this track.
     *
     * ⚠️ Do NOT use RestorationDiagnose.noiseFloorDb for this. It reports the
     * 10th-percentile bin energy, which is the right idea for archival tape
     * but reads the MUSIC as noise on anything dense: measured 2026-08-09, a
     * continuous full-level bed returns −13.7 dBFS and its own severity map
     * calls that "bad". Shipping it as a delivery check would fail nearly
     * every commercial rap or EDM master — precisely this tool's audience.
     *
     * A gap-aware measure instead: the quietest 400 ms window IS the floor,
     * but only if the track actually has a gap. When the quietest moment sits
     * within 20 dB of the typical level there is nowhere to hear noise, so we
     * return measurable:false and the UI says "не измерить" rather than
     * inventing a verdict.
     */
    function noiseFloorEstimate(series) {
        var vals = (series || []).slice();
        if (vals.length < 12) return { measurable: false, floorDb: NaN, snrDb: NaN };
        var typical = percentile(vals, 0.5);
        // p02 rather than the outright minimum — one anomalous window (a gap
        // between files, a dropout) should not define the floor.
        var floor = percentile(vals, 0.02);
        var snr = typical - floor;
        return { measurable: snr >= 20, floorDb: floor, snrDb: snr };
    }

    /**
     * Track edges — a hard cut and an editing click read the same to a
     * listener ("трек обрывается"), but they are different measurements:
     *   startPeakDb / endPeakDb — how loud the first / last 20 ms are. A
     *     master that stops at −6 dBFS was cut, not faded.
     *   edgeJumpRatio — the biggest sample-to-sample step in the first / last
     *     10 ms, DIVIDED BY the track's own 95th-percentile step.
     *   fadeDb — RMS of the last second minus RMS of the three before it.
     *     Strongly negative = there IS a fade / reverb tail.
     *
     * The ratio matters: an absolute threshold on |Δ| cannot work, because a
     * dense broadband master legitimately swings ~0.27 between neighbouring
     * samples (−11 dBFS) and would be reported as clicking from end to end,
     * while the same absolute step inside a quiet acoustic outro is a real
     * pop nobody would catch. What defines an edit click is that the step is
     * anomalous FOR THIS TRACK — so we normalise by the track's own step
     * distribution and the check becomes content-independent.
     */
    function edgeStats(buffer) {
        var sr = buffer.sampleRate, len = buffer.length, ch = buffer.numberOfChannels;
        var edge = Math.max(1, Math.round(sr * 0.02));   // 20 ms level window
        var jumpWin = Math.max(2, Math.round(sr * 0.01)); // 10 ms discontinuity window
        var startPeak = 0, endPeak = 0, startJump = 0, endJump = 0;

        for (var c = 0; c < ch; c++) {
            var d = buffer.getChannelData(c), i;
            for (i = 0; i < Math.min(edge, len); i++) startPeak = Math.max(startPeak, Math.abs(d[i]));
            for (i = Math.max(0, len - edge); i < len; i++) endPeak = Math.max(endPeak, Math.abs(d[i]));
            for (i = 1; i < Math.min(jumpWin, len); i++) startJump = Math.max(startJump, Math.abs(d[i] - d[i - 1]));
            for (i = Math.max(1, len - jumpWin); i < len; i++) endJump = Math.max(endJump, Math.abs(d[i] - d[i - 1]));
        }

        // Typical step size for THIS track — sampled (not every sample) so the
        // reference is cheap on a 10-minute file. p95 rather than the median so
        // ordinary transients set the bar and only true outliers clear it.
        // NB: the samples must be ADJACENT-pair deltas taken at intervals, not
        // deltas across the interval — |x[s] − x[s−stride]| is a much larger
        // quantity than |x[s] − x[s−1]| and comparing the edge jump against it
        // deflates the ratio by an order of magnitude, hiding real pops.
        var steps = [];
        var ref = buffer.getChannelData(0);
        var stride = Math.max(1, Math.floor(len / 20000));
        for (var s = stride; s < len; s += stride) steps.push(Math.abs(ref[s] - ref[s - 1]));
        var typical = steps.length ? percentile(steps, 0.95) : 0;
        var maxJump = Math.max(startJump, endJump);
        var jumpRatio = typical > 1e-6 ? (maxJump / typical) : (maxJump > 0.05 ? 99 : 0);

        // Envelope decay over the last 3 s, dB/s, from 0.5 s buckets. This is
        // the feature that separates a fade from a cut: across 34 finished
        // masters it ranges −52.4 … +0.6 dB/s, while the same tracks truncated
        // mid-music sit at −2.1 … +3.6.
        function rmsRange(fromSec, toSec) {
            var a = Math.max(0, Math.floor(len - toSec * sr));
            var b = Math.max(0, Math.floor(len - fromSec * sr));
            if (b <= a) return -Infinity;
            var sum = 0, n = 0;
            for (var cc = 0; cc < ch; cc++) {
                var dd = buffer.getChannelData(cc);
                for (var i2 = a; i2 < b; i2++) { sum += dd[i2] * dd[i2]; n++; }
            }
            return n > 0 ? db20(Math.sqrt(sum / n)) : -Infinity;
        }
        var last1 = rmsRange(0, 1), prev3 = rmsRange(1, 4);
        var decay = NaN;
        (function () {
            var ys = [], t;
            for (t = 6; t >= 1; t--) ys.push(rmsRange((t - 1) * 0.5, t * 0.5));
            var sx = 0, sy = 0, sxx = 0, sxy = 0, c = 0;
            ys.forEach(function (y, idx) {
                if (!isFinite(y)) return;
                var x = idx * 0.5;
                sx += x; sy += y; sxx += x * x; sxy += x * y; c++;
            });
            if (c >= 3) decay = (c * sxy - sx * sy) / (c * sxx - sx * sx);
        })();

        return {
            startPeakDb: db20(startPeak),
            endPeakDb: db20(endPeak),
            startJumpDb: db20(startJump),
            endJumpDb: db20(endJump),
            jumpRatio: jumpRatio,
            decayDbPerSec: decay,
            jumpWhere: (startJump >= endJump) ? 'start' : 'end',
            fadeDb: (isFinite(last1) && isFinite(prev3)) ? (last1 - prev3) : NaN
        };
    }

    /**
     * Arrangement shape from the short-term LUFS curve.
     *   introSec        — when the arrangement arrives: first moment the curve
     *                     comes within 3 LU of the track's own median. That is
     *                     "the track is now at full size", not "sound started".
     *   sectionSpreadLu — loudest 10 s window minus quietest 10 s window,
     *                     ignoring the first and last 15 s so a cold open or a
     *                     fade-out doesn't masquerade as a chorus.
     */
    function arrangementStats(st) {
        var out = { introSec: NaN, sectionSpreadLu: NaN };
        if (!st || !st.stLufs || st.stLufs.length < 40) return out;
        var t = st.timeSec, v = st.stLufs, n = v.length;

        var live = [];
        for (var i = 0; i < n; i++) if (isFinite(v[i]) && v[i] > -70) live.push(v[i]);
        if (live.length < 20) return out;
        var med = percentile(live, 0.5);

        for (var j = 0; j < n; j++) {
            if (isFinite(v[j]) && v[j] >= med - 3) { out.introSec = t[j]; break; }
        }

        // 10 s windows on a 100 ms hop, trimmed by 15 s at both ends.
        var hop = (st.hopMs || 100) / 1000;
        var wLen = Math.round(10 / hop), trim = Math.round(15 / hop);
        var a = trim, b = n - trim - wLen;
        if (b > a) {
            var lo = Infinity, hi = -Infinity;
            for (var w = a; w <= b; w += Math.max(1, Math.round(1 / hop))) {
                var sum = 0, c = 0;
                for (var k = w; k < w + wLen; k++) {
                    if (isFinite(v[k]) && v[k] > -70) { sum += v[k]; c++; }
                }
                if (c > wLen * 0.5) {
                    var m = sum / c;
                    if (m < lo) lo = m;
                    if (m > hi) hi = m;
                }
            }
            if (isFinite(lo) && isFinite(hi)) out.sectionSpreadLu = hi - lo;
        }
        return out;
    }

    /**
     * Per-band mono collapse, in dB. For each band: energy of the mono sum
     * (L+R)/2 against the average of the two channels' own energies.
     *   0 dB   — the band survives mono untouched (fully correlated)
     *  −3 dB   — half the energy cancels
     *  −6 dB + — the band largely disappears on a phone speaker
     * The mid PSD is already in the analyze() bundle, so only L and R cost an
     * extra pass; on mono files there is nothing to cancel and we return null.
     */
    function monoLoss(buffer) {
        if (!buffer || buffer.numberOfChannels < 2) return null;
        var ACE = window.AudioComparatorEngine;
        if (!ACE || !ACE.computeAveragePsd) return null;

        // All three spectra are measured over the SAME bounded window.
        //   · Same window, because comparing a full-track mid against a
        //     partial L/R would put the difference between two different
        //     spans into a number labelled "mono loss".
        //   · Bounded, because mono compatibility is a stationary property:
        //     90 s answers it as well as 9 minutes, and a full-length pass
        //     per channel adds seconds of wall clock on a long track for no
        //     extra information.
        var sr = buffer.sampleRate, len = buffer.length;
        var span = Math.min(len, Math.round(sr * 90));
        var off = Math.max(0, Math.floor((len - span) / 2));
        var L = buffer.getChannelData(0).subarray(off, off + span);
        var R = buffer.getChannelData(1).subarray(off, off + span);
        var M = new Float32Array(span);
        for (var i = 0; i < span; i++) M[i] = (L[i] + R[i]) * 0.5;

        // Duck-typed single-channel views — computeAveragePsd only reads
        // numberOfChannels / length / sampleRate / getChannelData.
        function view(d) {
            return {
                numberOfChannels: 1, length: d.length, sampleRate: sr,
                getChannelData: function () { return d; }
            };
        }
        var pL, pR, midPsd;
        try {
            pL = ACE.computeAveragePsd(view(L), { fftSize: 8192 });
            pR = ACE.computeAveragePsd(view(R), { fftSize: 8192 });
            midPsd = ACE.computeAveragePsd(view(M), { fftSize: 8192 });
        } catch (e) { return null; }
        if (!pL || !pL.freqHz.length || !pR || !pR.freqHz.length
            || !midPsd || !midPsd.freqHz.length) return null;

        var RANGES = [
            { key: 'low', lo: 40, hi: 160 },
            { key: 'mid', lo: 160, hi: 2000 },
            { key: 'high', lo: 2000, hi: 12000 }
        ];
        var bands = [], worst = null;
        RANGES.forEach(function (r) {
            var eMono = bandDensityDb(midPsd, r.lo, r.hi);
            var eL = bandDensityDb(pL, r.lo, r.hi), eR = bandDensityDb(pR, r.lo, r.hi);
            if (!isFinite(eMono) || !isFinite(eL) || !isFinite(eR)) return;
            var eStereo = db10((Math.pow(10, eL / 10) + Math.pow(10, eR / 10)) / 2);
            var loss = eMono - eStereo;
            bands.push({ key: r.key, lossDb: loss });
            // Seed from the first band rather than from a hard 0: a track where
            // every band SURVIVES mono has no negative loss, and seeding at 0
            // left `worst.key` null so the row silently vanished instead of
            // reporting the good news.
            if (!worst || loss < worst.lossDb) worst = { key: r.key, lossDb: loss };
        });
        return bands.length ? { bands: bands, worst: worst } : null;
    }

    /**
     * Edit clicks per minute, whole track.
     *
     * ⚠️ Written here rather than borrowed. RestorationDiagnose._clicksPerSec
     * fired on 34/34 finished masters (measured 2026-08-09) for two reasons:
     * it thresholds the FIRST difference against a GLOBAL scale, and its
     * `median` and `mad` are read from the same percentile so the threshold
     * collapses to 7× the median step. Fixing only the MAD makes it worse
     * (14 719 false clicks/min vs 9 852 — a correct MAD is smaller than the
     * median on a heavy-tailed distribution, so the bar drops).
     *
     * What works, measured against 10 real masters with clicks planted at
     * known densities:
     *  · SECOND difference — responds to a break in the waveform rather than
     *    to a steep-but-smooth slope (a bass swing has a huge first difference
     *    and a small second one);
     *  · a LOCAL 50 ms scale — music is non-stationary, and one global median
     *    is set by the loudest chorus, after which the quiet verse reads as
     *    full of clicks;
     *  · an ISOLATION test — a click is over within a fraction of a
     *    millisecond, a drum attack keeps producing large second differences
     *    for milliseconds. This single condition is what moved false
     *    positives from 729/min to 13/min;
     *  · an AUDIBILITY gate — the break must be large next to the local
     *    waveform, not merely next to the local smoothness.
     *
     * Operating point k=50 / iso=0.15 / relAmp=0.10 from the density sweep:
     * finished masters land at 0.0-3.3 clicks/min (the worst being lofi,
     * which contains deliberate vinyl crackle — a true positive), while a
     * planted 10/min reads 6.1-13.3. Hence the thresholds in evaluate().
     *
     * Reports a RATE, and the copy says "a click problem", not "3 clicks":
     * individual clicks below ~6/min are inside the noise of this measurement
     * and claiming them would be false precision.
     */
    function clickRate(buffer, opts) {
        opts = opts || {};
        var k = opts.k || 50, iso = opts.iso || 0.15, relAmp = opts.relAmp || 0.10;
        var sr = buffer.sampleRate, n = buffer.length, ch = buffer.numberOfChannels;
        if (n < sr) return 0;
        var chans = [];
        for (var c = 0; c < ch; c++) chans.push(buffer.getChannelData(c));

        // Mono sum, and the second difference in one pass.
        var d2 = new Float32Array(n), mono = new Float32Array(n);
        var i;
        for (i = 0; i < n; i++) {
            var s = 0;
            for (var c2 = 0; c2 < ch; c2++) s += chans[c2][i];
            mono[i] = s / ch;
        }
        for (i = 2; i < n; i++) d2[i] = Math.abs(mono[i] - 2 * mono[i - 1] + mono[i - 2]);

        var win = Math.max(256, Math.floor(sr * 0.05));
        var refr = Math.max(8, Math.floor(0.001 * sr));
        var tail = Math.max(8, Math.floor(sr * 0.002));
        var scratch = new Float64Array(512);
        var count = 0, last = -refr;

        for (var start = 0; start < n; start += win) {
            var end = Math.min(n, start + win);
            var step = Math.max(1, Math.floor((end - start) / 512));
            var m = 0, peak = 0;
            for (i = start; i < end && m < 512; i += step) {
                scratch[m++] = d2[i];
                var a = Math.abs(mono[i]);
                if (a > peak) peak = a;
            }
            if (!m) continue;
            var sorted = scratch.subarray(0, m).slice().sort();
            var med = sorted[m >> 1] || 1e-9;
            var thr = med * k, ampThr = peak * relAmp;
            for (i = Math.max(2, start); i < end; i++) {
                if (d2[i] <= thr || (i - last) <= refr || d2[i] < ampThr) continue;
                var after = 0, cnt = 0;
                for (var j = i + 3; j < Math.min(n, i + tail); j++) { after += d2[j]; cnt++; }
                if (cnt && (after / cnt) < d2[i] * iso) { count++; last = i; }
            }
        }
        return count / (n / sr) * 60;
    }


    /**
     * Stationary resonance — the one spectral defect that survived measurement.
     *
     * Credit where due: this is Stas Zabegin's criterion. Two earlier attempts
     * failed because they could not tell a resonance from a REPEATING RIFF —
     * both sit in most frames. His discriminator is harmony: a musical note
     * moves when the chord changes, a room mode or a cabinet resonance does
     * not. So prominence is counted across distinct CHROMA STATES, not across
     * frames.
     *
     * Measured on 34 real masters (2026-08-09): clean coverage median 22 %,
     * p90 37 %, max 61 %. A ringing resonator planted into six of them reads
     * 66-100 %. Hence the 55 % bar — it fires on 1/34 finished masters, and
     * that one is `metal` at 2468 Hz, which is almost certainly a genuine
     * guitar-cabinet resonance rather than a false alarm.
     *
     * ⚠️ The plant has to RING. A peaking EQ on a track with little content at
     * f0 changes nothing audible and reads as a miss — the earlier "blind
     * detector" verdict was partly a broken planting method, the same trap as
     * using a low shelf to test a sub-vs-bass ratio.
     *
     * Deliberately reports only the strongest one, as a note, never a failure:
     * whether a stationary peak is a defect or the sound of the record is a
     * judgement the meter does not get to make.
     */
    function stationaryResonance(buffer, opts) {
        opts = opts || {};
        var ACE = window.AudioComparatorEngine;
        if (!ACE || !ACE.__fftRadix2) return null;
        var lo = opts.lo || 200, hi = opts.hi || 9000;
        var excessDb = opts.excessDb || 4, fft = opts.fft || 4096;
        var sr = buffer.sampleRate, n = buffer.length, ch = buffer.numberOfChannels;
        if (n < fft * 8) return null;

        var chans = [], c;
        for (c = 0; c < ch; c++) chans.push(buffer.getChannelData(c));
        var bands = [], f;
        for (f = lo; f < hi; f *= Math.pow(2, 1 / 12)) bands.push(f * Math.pow(2, 1 / 24));
        var win = new Float32Array(fft), i;
        for (i = 0; i < fft; i++) win[i] = 0.5 - 0.5 * Math.cos(2 * Math.PI * i / fft);
        var re = new Float64Array(fft), im = new Float64Array(fft);
        var binHz = sr / fft, nFrames = Math.floor(n / fft);
        var states = {}, keys = [];
        var bandDb = new Float64Array(bands.length);
        var chroma = new Float64Array(12);

        for (var fi = 0; fi < nFrames; fi++) {
            var off = fi * fft, energy = 0, v, k;
            for (i = 0; i < fft; i++) {
                v = 0;
                for (c = 0; c < ch; c++) v += chans[c][off + i];
                v /= ch;
                energy += v * v; re[i] = v * win[i]; im[i] = 0;
            }
            if (Math.sqrt(energy / fft) < 0.003) continue;
            ACE.__fftRadix2(re, im, false);

            for (i = 0; i < 12; i++) chroma[i] = 0;
            for (k = Math.floor(80 / binHz); k < Math.floor(2000 / binHz); k++) {
                var pc = ((Math.round(12 * Math.log(k * binHz / 440) / Math.LN2) % 12) + 12) % 12;
                chroma[pc] += re[k] * re[k] + im[k] * im[k];
            }
            var a = 0, b = 1;
            for (i = 1; i < 12; i++) if (chroma[i] > chroma[a]) a = i;
            for (i = 0; i < 12; i++) if (i !== a && (b === a || chroma[i] > chroma[b])) b = i;
            var key = Math.min(a, b) + ':' + Math.max(a, b);

            for (var bi = 0; bi < bands.length; bi++) {
                var f0 = bands[bi] / Math.pow(2, 1 / 24), f1 = bands[bi] * Math.pow(2, 1 / 24);
                var i0 = Math.max(1, Math.floor(f0 / binHz)), i1 = Math.min(fft / 2 - 1, Math.ceil(f1 / binHz));
                var s2 = 0, c2 = 0;
                for (k = i0; k <= i1; k++) { s2 += re[k] * re[k] + im[k] * im[k]; c2++; }
                bandDb[bi] = c2 ? db10(s2 / c2) : NaN;
            }
            if (!states[key]) { states[key] = { frames: 0, hits: new Float64Array(bands.length) }; keys.push(key); }
            var st = states[key];
            st.frames++;
            for (bi = 0; bi < bands.length; bi++) {
                var a0 = Math.max(0, bi - 12), a1 = Math.min(bands.length - 1, bi + 12), sn = 0, cn = 0;
                for (k = a0; k <= a1; k++) { if (k === bi || !isFinite(bandDb[k])) continue; sn += Math.pow(10, bandDb[k] / 10); cn++; }
                if (!cn || !isFinite(bandDb[bi])) continue;
                if (bandDb[bi] - db10(sn / cn) > excessDb) st.hits[bi]++;
            }
        }

        var solid = [];
        for (i = 0; i < keys.length; i++) if (states[keys[i]].frames >= 3) solid.push(states[keys[i]]);
        // Too few harmonic states means the track never changes chord, and the
        // whole discriminator is unavailable — say so instead of guessing.
        if (solid.length < 8) return { measurable: false, coverage: 0, hz: 0, states: solid.length };
        var best = 0, bestHz = 0;
        for (var bj = 0; bj < bands.length; bj++) {
            var inStates = 0;
            for (i = 0; i < solid.length; i++) if (solid[i].hits[bj] / solid[i].frames > 0.5) inStates++;
            var cov = inStates / solid.length;
            if (cov > best) { best = cov; bestHz = bands[bj]; }
        }
        return { measurable: true, coverage: best, hz: bestHz, states: solid.length };
    }


    /**
     * Exact vocal-vs-beat balance from a supplied vocal STEM.
     *
     * Everything cheaper was measured and rejected: mid/side proxies move only
     * 0.26-0.34× with the truth, MDX separation costs 31 minutes and still
     * errs 2.5 dB, and estimating the beat from the pauses between phrases
     * fails because the gaps are filled by HARMONIC answering parts — measured
     * on ground-truth stems, the harmonic backing sits 8.68 dB above the drums
     * in those gaps, so no percussion removal can rescue it (Fitzgerald HPSS
     * tops out at ~4 dB of drum rejection anyway).
     *
     * With the stem in hand the problem is arithmetic, not estimation.
     *
     * Two details that make it robust rather than merely correct:
     *   · the stem may be at a different gain than it sits at in the mix, so
     *     we solve for the scalar that best explains the mix,
     *         alpha = <mix, stem> / <stem, stem>
     *     which is the least-squares projection. The instrumental is then the
     *     residual mix − alpha·stem.
     *   · that same projection tells us whether the stem BELONGS to this mix:
     *     if it does, subtracting it must remove energy. `reduction` is how
     *     much the residual dropped. A stem from another render, another
     *     song, or misaligned in time removes little or nothing — and the row
     *     must then say "не совпадает", never print a confident number.
     */
    function vocalBalance(mix, stem, opts) {
        opts = opts || {};
        if (!mix || !stem) return null;
        var lo = opts.lo || 300, hi = opts.hi || 3400;
        var sr = mix.sampleRate;
        if (stem.sampleRate !== sr) return { measurable: false, reason: 'rate' };
        var n = Math.min(mix.length, stem.length);
        // A stem trimmed to a different length is a different render; a few
        // hundred ms of tolerance covers encoder padding, no more.
        if (Math.abs(mix.length - stem.length) > sr * 0.5) return { measurable: false, reason: 'length' };
        if (n < sr) return { measurable: false, reason: 'short' };

        var mch = mix.numberOfChannels, sch = stem.numberOfChannels;
        var M = new Float32Array(n), V = new Float32Array(n), i, c;
        for (i = 0; i < n; i++) {
            var m = 0, v = 0;
            for (c = 0; c < mch; c++) m += mix.getChannelData(c)[i];
            for (c = 0; c < sch; c++) v += stem.getChannelData(c)[i];
            M[i] = m / mch; V[i] = v / sch;
        }

        // least-squares projection of the mix onto the stem
        var dot = 0, vv = 0, mm = 0;
        for (i = 0; i < n; i++) { dot += M[i] * V[i]; vv += V[i] * V[i]; mm += M[i] * M[i]; }
        if (vv <= 1e-12) return { measurable: false, reason: 'silent' };
        var alpha = dot / vv;
        // Correlation, not energy reduction, decides whether the stem BELONGS.
        // Reduction conflates "wrong stem" with "very quiet vocal": a genuine
        // stem sitting 12 dB down removes only ~0.5 dB from the mix, which is
        // exactly where an energy threshold would start refusing real input.
        var corr = dot / Math.sqrt(Math.max(1e-20, mm * vv));
        var resid = new Float32Array(n), rr = 0;
        for (i = 0; i < n; i++) { resid[i] = M[i] - alpha * V[i]; rr += resid[i] * resid[i]; }
        var reductionDb = db10(mm / Math.max(1e-20, rr));
        // Threshold from measurement: a real stem correlates 0.19-0.83 across
        // a −12…+9 dB gain sweep, an alien one lands near zero.
        if (!(corr > 0.10)) return { measurable: false, reason: 'mismatch', corr: corr, reductionDb: reductionDb };

        function bandRms(sig) {
            // 4th-order band-pass around the speech core, matching the
            // checklist's 300-3400 Hz window.
            var out = Float32Array.from(sig);
            bandpass(out, sr, lo, hi);
            var s = 0;
            for (var j = 0; j < out.length; j++) s += out[j] * out[j];
            return db10(s / out.length);
        }
        var scaled = new Float32Array(n);
        for (i = 0; i < n; i++) scaled[i] = alpha * V[i];
        var vocalDb = bandRms(scaled), beatDb = bandRms(resid);
        return {
            measurable: true, alpha: alpha, reductionDb: reductionDb, corr: corr,
            vocalDb: vocalDb, beatDb: beatDb, deltaDb: vocalDb - beatDb
        };
    }

    // Cascaded RBJ high-pass + low-pass, applied in place.
    function bandpass(x, sr, lo, hi) {
        function biquad(b0, b1, b2, a1, a2) {
            var x1 = 0, x2 = 0, y1 = 0, y2 = 0;
            for (var i = 0; i < x.length; i++) {
                var xi = x[i];
                var y = b0 * xi + b1 * x1 + b2 * x2 - a1 * y1 - a2 * y2;
                x2 = x1; x1 = xi; y2 = y1; y1 = y; x[i] = y;
            }
        }
        function hp(f) {
            var w = 2 * Math.PI * f / sr, c = Math.cos(w), s = Math.sin(w), al = s / (2 * 0.7071), a0 = 1 + al;
            biquad((1 + c) / 2 / a0, -(1 + c) / a0, (1 + c) / 2 / a0, (-2 * c) / a0, (1 - al) / a0);
        }
        function lp(f) {
            var w = 2 * Math.PI * f / sr, c = Math.cos(w), s = Math.sin(w), al = s / (2 * 0.7071), a0 = 1 + al;
            biquad((1 - c) / 2 / a0, (1 - c) / a0, (1 - c) / 2 / a0, (-2 * c) / a0, (1 - al) / a0);
        }
        hp(lo); hp(lo); lp(hi); lp(hi);
    }

    /* ── Measurement pass ──────────────────────────────────────────────── */

    /**
     * Everything the verdict needs that analyze() doesn't already produce.
     * Split from evaluate() on purpose: switching the genre dropdown must
     * re-grade instantly without re-measuring the file.
     *
     * @param buffer  decoded AudioBuffer
     * @param bundle  MixAnalyzer.analyze(buffer) output (reused, not recomputed)
     * @param opts    { stLufs } — pass the short-term curve if the page already
     *                built it for the loudness chart; otherwise we build it.
     */
    function measure(buffer, bundle, opts) {
        opts = opts || {};
        // opts.vocalStem — an optional AudioBuffer the user supplied. Only
        // with it does the vocal-vs-beat balance become arithmetic instead of
        // estimation; everything cheaper was measured and rejected.
        var vocal = opts.vocalStem ? vocalBalance(buffer, opts.vocalStem) : null;
        var psd = bundle && bundle.psd;
        var ACE = window.AudioComparatorEngine;

        var st = opts.stLufs;
        if (!st && ACE && ACE.measureShortTermLufsOverTime) {
            try { st = ACE.measureShortTermLufsOverTime(buffer); } catch (e) { st = null; }
        }

        var levels = levelSeries(buffer);

        return {
            dr: dynamicRangeDb(levels),
            noise: noiseFloorEstimate(levels),
            edges: edgeStats(buffer),
            arrangement: arrangementStats(st),
            mono: monoLoss(buffer),
            clicksPerMin: clickRate(buffer),
            vocal: vocal,
            resonance: stationaryResonance(buffer),
            // ⛔ No resonance / sibilance / air-hump rows. Two independent
            // approaches were measured and BOTH failed on real music:
            //   · excess over a smoothed whole-track PSD — separation was
            //     NEGATIVE at all 12 narrow/wide width pairs, because a real
            //     track's own spectrum carries stationary peaks as strong as
            //     a planted +12 dB resonance;
            //   · per-frame persistence — also negative absolutely (metal's
            //     own 2.5 kHz content sits at 58 % of frames, above a planted
            //     resonance in a sparse track at 43 %).
            // Persistence DID localise correctly (a 3000 Hz plant moved the
            // peak to 3109 Hz every time) but only relative to the same track
            // without the defect, which by definition we never have. A row
            // that can only ever answer "ok" tells the user their track was
            // checked when it was not. See the memory note before retrying.
            // Tonal ratios the checklist asks for by name. Sub-vs-bass and
            // rumble compare unequal-width neighbours, so they use TOTAL band
            // energy; the mid dip compares a narrow window against much wider
            // ones and only makes sense as density. See bandEnergyDb.
            // (hf_slope stays removed — see below.)
        // ⛔ hf_slope removed too: it fired on 0/34 real masters and its
            // sensitivity was never established, which is the same
            // unverified state the four spectral rows were removed for.
            // Restoring it means planting a rising top end first.
            subVsBassDb: bandEnergyDb(psd, 20, 60) - bandEnergyDb(psd, 60, 120),
            rumbleVsSubDb: bandEnergyDb(psd, 20, 30) - bandEnergyDb(psd, 30, 60)
            // ⛔ No mid-dip row either. The metric was biased — comparing
            // 500-1000 Hz against the LOUDER of its neighbours reads −7 to
            // −10 dB on clean jazz, romance and lullaby masters, because
            // ordinary music simply has less density there. And the page
            // already answers this question properly: the 9-band / 4-zone
            // tonal comparison is a better answer to "are my mids scooped"
            // than a second, worse one bolted onto the verdict card.
        };
    }

    /* ── Grading ───────────────────────────────────────────────────────── */

    /**
     * Genre-specific arrangement expectations. Present ONLY for the genres
     * where "commercial length" is a real convention — asking a classical
     * recording to finish inside 4 minutes is not a defect, it's a category
     * error, so those genres simply have no arrangement rows.
     */
    var ARRANGEMENT = {
        edm:    { durSec: [150, 330], introSec: 30 },
        hiphop: { durSec: [105, 270], introSec: 15 },
        pop:    { durSec: [120, 270], introSec: 15 },
        rock:   { durSec: [120, 330], introSec: 25 }
    };

    function row(block, id, status, args) {
        return { block: block, id: id, status: status, args: args || {} };
    }

    /**
     * Grade a measured track. `genreKey` selects the balance targets and may
     * be null — in reference mode the balance block is the compare engine's
     * job, and only delivery + defects apply.
     *
     * Returns { delivery: [row], defects: [row], balance: [row], summary }.
     */
    function evaluate(bundle, extra, genreKey) {
        var delivery = [], defects = [], balance = [];
        // Normalise once instead of guarding at 20 call sites: a missing
        // measurement must yield an empty verdict, never a thrown page.
        extra = extra || {};
        var tech = (bundle && bundle.tech) || {};
        var edges = extra.edges || {};
        var diag = extra.diag || null;

        /* ── DELIVERY — objective, same in every genre ─────────────────── */

        // True peak. −1 dBTP is the universal streaming ceiling; above 0 the
        // file is already clipping the DAC on playback, not just "hot".
        var tp = bundle.truePeak;
        if (isFinite(tp)) {
            delivery.push(row('delivery', 'true_peak',
                tp > 0 ? FAIL : (tp > -1 ? WARN : PASS), { tp: fx(tp, 1) }));
        }

        // Samples pinned at full scale. 8 is the threshold Matchering uses to
        // call a file clipped at all; 0.02 % of samples is audible distortion.
        if (tech.clipCount != null) {
            delivery.push(row('delivery', 'clipping',
                tech.clipPct >= 0.02 ? FAIL : (tech.clipCount >= 8 ? WARN : PASS),
                { n: tech.clipCount, pct: fx(tech.clipPct, 3) }));
        }

        // DC offset eats headroom symmetrically and signals a missing HPF.
        if (tech.dcMax != null) {
            delivery.push(row('delivery', 'dc_offset',
                tech.dcMax > 0.02 ? FAIL : (tech.dcMax > 0.008 ? WARN : PASS),
                { v: fx(tech.dcMax, 4) }));
        }

        // Phase correlation below zero means the two channels fight each
        // other — the track partly vanishes the moment anything sums to mono.
        if (bundle.stereo && !bundle.stereo.isMono && isFinite(bundle.stereo.phaseCorrelation)) {
            var pc = bundle.stereo.phaseCorrelation;
            delivery.push(row('delivery', 'phase',
                pc < 0 ? FAIL : (pc < 0.2 ? WARN : PASS), { corr: fx(pc, 2) }));
        }

        // Bass below ~150 Hz is summed to mono by club rigs and phone
        // speakers, so anti-phase low end is a delivery fault, not a taste.
        if (isFinite(bundle.bassCorr) && bundle.channels >= 2) {
            delivery.push(row('delivery', 'bass_mono',
                bundle.bassCorr < 0.5 ? FAIL : (bundle.bassCorr < 0.7 ? WARN : PASS),
                { corr: fx(bundle.bassCorr, 2) }));
        }

        // Edit clicks. Thresholds are the measured separation, not a guess:
        // finished masters top out at 3.3/min, a planted 10/min reads 6.1+.
        if (isFinite(extra.clicksPerMin)) {
            delivery.push(row('delivery', 'clicks',
                extra.clicksPerMin >= 20 ? FAIL : (extra.clicksPerMin >= 6 ? WARN : PASS),
                { rate: fx(extra.clicksPerMin, 1) }));
        }

        // Noise in the gaps. Reports NA — not a pass — on a wall-of-sound
        // master where there is no gap to listen into; see noiseFloorEstimate.
        var nf = extra.noise;
        if (nf) {
            delivery.push(row('delivery', 'noise_floor',
                !nf.measurable ? NA
                    : (nf.snrDb < 40 ? WARN : PASS),
                { snr: isFinite(nf.snrDb) ? fx(nf.snrDb, 0) : '',
                  floor: isFinite(nf.floorDb) ? fx(nf.floorDb, 0) : '' }));
        }

        // ⛔ NO mains-hum row in wave 1. RestorationDiagnose.mainsHz reports a
        // 59 Hz "hum" at −31 dB on pink noise that contains none (measured
        // 2026-08-09) — it cannot tell a steady mains tone from an ordinary
        // bass note sitting at the same frequency. Separating them needs
        // narrowband stability-over-time analysis, which is its own job.

        // A track that stops mid-music was cut, not ended — but the test has
        // to be the ENVELOPE, not the level. Judging by level alone flagged
        // 9 of 34 finished masters, because a real fade routinely stops above
        // −40 dBFS. Requiring the last three seconds to be FALLING as well
        // takes that to 1 of 34 (punk-rock, which decays only in its final
        // 100 ms and is arguably abrupt) with detection still 34/34 on
        // deliberately truncated tracks.
        if (isFinite(edges.endPeakDb)) {
            // −30 dBFS, not −40: reggae ends at −39.4 dBFS with a gentle
            // −2.6 dB/s decay and was still flagged, which is absurd — at that
            // level there is nothing left to cut off. Truncated tracks land at
            // −20.3 … −3.2 dBFS, so the gate keeps every real cut.
            var loudEnd = edges.endPeakDb > -30;
            var falling = isFinite(edges.decayDbPerSec) && edges.decayDbPerSec < -3;
            delivery.push(row('delivery', 'tail',
                (loudEnd && !falling) ? (edges.endPeakDb > -20 ? FAIL : WARN) : PASS,
                { db: fx(edges.endPeakDb, 1), slope: fx(edges.decayDbPerSec, 1) }));
        }

        // Discontinuity in the first / last 10 ms — the non-zero-crossing pop,
        // measured against this track's own typical sample step (see edgeStats).
        if (isFinite(edges.jumpRatio)) {
            delivery.push(row('delivery', 'edge_click',
                edges.jumpRatio > 8 ? FAIL : (edges.jumpRatio > 4 ? WARN : PASS),
                { x: fx(edges.jumpRatio, 1), where: edges.jumpWhere }));
        }

        /* ── DEFECTS — genre-independent artefacts, advisory ───────────── */

        // Energy under 30 Hz that outweighs the usable sub — inaudible on
        // every consumer system, but it still steals limiter headroom.
        if (isFinite(extra.rumbleVsSubDb)) {
            defects.push(row('defects', 'rumble',
                extra.rumbleVsSubDb > 3 ? STRONG : (extra.rumbleVsSubDb > 0 ? NOTE : OK),
                { db: fx(extra.rumbleVsSubDb, 1) }));
        }

        // Stationary resonance. 55 % is the measured bar: over 34 finished
        // masters clean coverage runs 14-61 % (median 22, p90 37) while a
        // planted ringing resonator reads 66-100 %.
        var rz = extra.resonance;
        if (rz) {
            defects.push(row('defects', 'resonance',
                !rz.measurable ? NA : (rz.coverage >= 0.75 ? STRONG : (rz.coverage >= 0.55 ? NOTE : OK)),
                { hz: Math.round(rz.hz), pct: Math.round(rz.coverage * 100) }));
        }

        // What disappears when the track is summed to mono.
        if (extra.mono && extra.mono.worst.key) {
            var w = extra.mono.worst;
            defects.push(row('defects', 'mono_loss',
                w.lossDb <= -4 ? STRONG : (w.lossDb <= -1.5 ? NOTE : OK),
                { band: w.key, db: fx(w.lossDb, 1) }));
        }

        /* ── BALANCE — genre-relative, never a failure ─────────────────── */

        var G = (window.MixAnalyzer && window.MixAnalyzer.GENRE_TARGETS) || {};
        var target = genreKey ? G[genreKey] : null;

        if (target) {
            // Integrated loudness against the genre anchor, not against −14.
            if (isFinite(bundle.lufs) && isFinite(target.lufs)) {
                var dL = bundle.lufs - target.lufs;
                balance.push(row('balance', 'lufs',
                    Math.abs(dL) >= 4 ? STRONG : (Math.abs(dL) >= 2 ? NOTE : OK),
                    // ⛔ No `dir: 'louder'|'quieter'` arg here (nor on plr /
                    // stereo_corr below). The copy is phrased to read correctly
                    // in both directions from the numbers alone, and an untranslated
                    // enum arg sitting in the payload is a loaded gun: the first
                    // locale to write `:dir` gets an English word on screen. If
                    // direction-specific wording is ever wanted, add it the way
                    // `where` / `band` are done — with translated tokens.
                    { mine: fx(bundle.lufs, 1), target: fx(target.lufs, 1),
                      db: fx(Math.abs(dL), 1) }));
            }

            // Peak headroom (PLR) — how hard the limiter is working.
            if (isFinite(bundle.plr) && isFinite(target.plr)) {
                var dP = bundle.plr - target.plr;
                balance.push(row('balance', 'plr',
                    Math.abs(dP) >= 4 ? STRONG : (Math.abs(dP) >= 2 ? NOTE : OK),
                    { mine: fx(bundle.plr, 1), target: fx(target.plr, 1) }));
            }

            // Macro-dynamics across the whole track.
            if (isFinite(bundle.lra) && isFinite(target.lra)) {
                var dR = bundle.lra - target.lra;
                balance.push(row('balance', 'lra',
                    Math.abs(dR) >= 5 ? STRONG : (Math.abs(dR) >= 3 ? NOTE : OK),
                    { mine: fx(bundle.lra, 1), target: fx(target.lra, 1) }));
            }

            // DR — the loud-vs-quiet spread the checklist asks for by name.
            if (isFinite(extra.dr)) {
                balance.push(row('balance', 'dr',
                    (extra.dr < 5 || extra.dr > 18) ? STRONG
                        : ((extra.dr < 8 || extra.dr > 14) ? NOTE : OK),
                    { db: fx(extra.dr, 1) }));
            }

            // A chorus that jumps more than ~3 LU over the track's own average
            // level usually means the section was never gain-staged.
            if (isFinite(bundle.shortTermMax) && isFinite(bundle.lufs)) {
                var over = bundle.shortTermMax - bundle.lufs;
                balance.push(row('balance', 'short_peak',
                    over > 5 ? STRONG : (over > 3 ? NOTE : OK), { lu: fx(over, 1) }));
            }

            // Sub against the usable bass — the "phone buzzes" check.
            if (isFinite(extra.subVsBassDb)) {
                balance.push(row('balance', 'sub_balance',
                    extra.subVsBassDb > 8 ? STRONG : (extra.subVsBassDb > 6 ? NOTE : OK),
                    { db: fx(extra.subVsBassDb, 1) }));
            }

                // Stereo correlation window — wide enough to have an image, tight
            // enough to keep a centre.
            if (bundle.stereo && !bundle.stereo.isMono && isFinite(bundle.stereo.phaseCorrelation)) {
                var c3 = bundle.stereo.phaseCorrelation;
                balance.push(row('balance', 'stereo_corr',
                    (c3 > 0.95 || c3 < 0.3) ? NOTE : OK,
                    { corr: fx(c3, 2) }));
            }

            // Arrangement — only for genres where length is a convention.
            var arr = ARRANGEMENT[genreKey];
            if (arr && isFinite(bundle.duration)) {
                var d2 = bundle.duration;
                balance.push(row('balance', 'duration',
                    (d2 < arr.durSec[0] || d2 > arr.durSec[1]) ? NOTE : OK,
                    { sec: Math.round(d2), min: arr.durSec[0], max: arr.durSec[1] }));
            }
            if (arr && extra.arrangement && isFinite(extra.arrangement.introSec)) {
                balance.push(row('balance', 'intro',
                    extra.arrangement.introSec > arr.introSec ? NOTE : OK,
                    { sec: Math.round(extra.arrangement.introSec), max: arr.introSec }));
            }
            if (extra.arrangement && isFinite(extra.arrangement.sectionSpreadLu)) {
                var sp = extra.arrangement.sectionSpreadLu;
                balance.push(row('balance', 'section_contrast',
                    (sp < 1 || sp > 6) ? NOTE : OK, { lu: fx(sp, 1) }));
            }
        }

        // Vocal vs beat — present only when the user supplied a stem, because
        // that is the only way this number is trustworthy. Thresholds are the
        // checklist's own: below +2 dB the vocal is buried, above +8 it shouts.
        var vb = extra.vocal;
        if (vb) {
            if (!vb.measurable) {
                balance.push(row('balance', 'vocal_balance', NA, { reason: vb.reason || 'mismatch' }));
            } else {
                var d = vb.deltaDb;
                balance.push(row('balance', 'vocal_balance',
                    (d < 0 || d > 10) ? STRONG : ((d < 2 || d > 8) ? NOTE : OK),
                    { db: fx(d, 1), corr: fx(vb.corr, 2) }));
            }
        }

        /* ── Summary ───────────────────────────────────────────────────── */

        var fails = 0, warns = 0, notes = 0;
        delivery.forEach(function (r) { if (r.status === FAIL) fails++; else if (r.status === WARN) warns++; });
        defects.concat(balance).forEach(function (r) {
            if (r.status === STRONG) notes += 2; else if (r.status === NOTE) notes++;
        });

        // Deliberately NOT a 0-100 "commercial score". A single number invites
        // the reading «84 % — значит можно релизить», which the measurements
        // cannot support: nothing here hears the song. The verdict says what
        // is broken (delivery) and what is unusual (the rest), and stops there.
        var verdict = fails > 0 ? 'blocked' : (warns > 0 ? 'warn' : (notes > 2 ? 'notes' : 'clean'));

        return {
            delivery: delivery, defects: defects, balance: balance,
            summary: { fails: fails, warns: warns, notes: notes, verdict: verdict,
                       genre: genreKey || null }
        };
    }

    window.MixReadiness = {
        measure: measure,
        evaluate: evaluate,
        // exported for tests
        dynamicRangeDb: dynamicRangeDb,
        levelSeries: levelSeries,
        noiseFloorEstimate: noiseFloorEstimate,
        edgeStats: edgeStats,
        arrangementStats: arrangementStats,
        monoLoss: monoLoss,
        clickRate: clickRate,
        vocalBalance: vocalBalance,
        stationaryResonance: stationaryResonance,
        bandDensityDb: bandDensityDb,
        bandEnergyDb: bandEnergyDb,
        percentile: percentile,
        ARRANGEMENT: ARRANGEMENT,
        version: '2.0.0'
    };
})();
